
import React, { useState, useEffect } from 'react';
import { getVersionInfo, checkForUpdates, invalidateCache } from '@/config/version';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';

const VersionIndicator = () => {
  const [versionInfo, setVersionInfo] = useState(getVersionInfo());
  const [isVisible, setIsVisible] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState<string>('');

  useEffect(() => {
    // Enhanced version info logging
    console.log('🚀 App Version Info:', {
      version: versionInfo.version,
      deploymentId: versionInfo.deploymentId,
      buildTimestamp: new Date(versionInfo.buildTimestamp).toLocaleString(),
      environment: versionInfo.environment,
      cacheKey: versionInfo.cacheKey
    });
    
    // Log device info
    console.log('📱 Device Info:', {
      isMobile: versionInfo.isMobile,
      viewport: versionInfo.viewport,
      userAgent: versionInfo.userAgent.substring(0, 100) + '...'
    });

    // Check for updates on mount
    handleCheckUpdates();
  }, []);

  const handleCheckUpdates = async () => {
    setIsChecking(true);
    try {
      const updateInfo = await checkForUpdates();
      setHasUpdate(updateInfo.hasUpdate);
      setLastCheck(new Date().toLocaleTimeString());
      
      if (updateInfo.hasUpdate) {
        console.log('🆕 Update available:', updateInfo.newVersion);
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleForceRefresh = async () => {
    try {
      console.log('🔄 Force refreshing application...');
      await invalidateCache();
      window.location.reload();
    } catch (error) {
      console.error('Failed to force refresh:', error);
    }
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const getStatusColor = () => {
    if (hasUpdate) return 'bg-orange-600 hover:bg-orange-700';
    return 'bg-blue-600 hover:bg-blue-700';
  };

  const getStatusIcon = () => {
    if (hasUpdate) return <AlertCircle className="w-3 h-3" />;
    return <CheckCircle className="w-3 h-3" />;
  };

  return (
    <>
      {/* Version toggle button with status indicator */}
      <button
        onClick={toggleVisibility}
        className={`fixed bottom-4 right-4 z-50 text-white p-2 rounded-full text-xs font-mono shadow-lg transition-colors flex items-center gap-1 ${getStatusColor()}`}
        style={{ fontSize: '10px' }}
        title={hasUpdate ? 'Update available' : 'App up to date'}
      >
        {getStatusIcon()}
        v{versionInfo.version}
        {hasUpdate && <span className="animate-pulse">!</span>}
      </button>

      {/* Enhanced version details card */}
      {isVisible && (
        <Card className="fixed bottom-16 right-4 z-50 p-4 bg-white shadow-xl border max-w-sm">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">Version Info</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCheckUpdates}
                disabled={isChecking}
                className="h-6 px-2 text-xs"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${isChecking ? 'animate-spin' : ''}`} />
                Check
              </Button>
            </div>
            
            <div className="text-xs space-y-1">
              <div><strong>Version:</strong> {versionInfo.version}</div>
              <div><strong>Deployment:</strong> {versionInfo.deploymentId}</div>
              <div><strong>Built:</strong> {new Date(versionInfo.buildTimestamp).toLocaleString()}</div>
              <div><strong>Environment:</strong> {versionInfo.environment}</div>
              <div><strong>Device:</strong> {versionInfo.isMobile ? 'Mobile' : 'Desktop'}</div>
              <div><strong>Viewport:</strong> {versionInfo.viewport.width}x{versionInfo.viewport.height}</div>
              <div className="text-green-600">
                <strong>Cache Key:</strong> {versionInfo.cacheKey}
              </div>
              {lastCheck && (
                <div className="text-gray-500">
                  <strong>Last Check:</strong> {lastCheck}
                </div>
              )}
            </div>

            {hasUpdate && (
              <div className="pt-2 border-t">
                <div className="text-orange-600 text-xs mb-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Update available
                </div>
                <Button
                  size="sm"
                  onClick={handleForceRefresh}
                  className="w-full text-xs h-7"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Force Refresh
                </Button>
              </div>
            )}

            <div className="pt-2 border-t">
              <Button
                size="sm"
                variant="outline"
                onClick={handleForceRefresh}
                className="w-full text-xs h-7"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Clear Cache & Reload
              </Button>
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default VersionIndicator;
